import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {RightPanel} from '../right-panel.class';
import {ActivatedRoute, Router} from '@angular/router';
import {ConfigurationService} from '../../../../../configuration/configuration.service';
import {AssetsManagerService} from '../../../../../data/assets/assets-manager.service';
import {EventManagerService} from '../../../../../data/events/event-manager.service';
import {EventRecord, EventType} from '../../../../../shared/models/event.model';
import {ActionMenuItem, ActionMenuItemSubMenu} from '../../../../../shared/models/action-menu-item.class';
import {ShortDateOrTimePipe} from '../../../../../shared/formatting/short-date-or-time.pipe';
import {DrawerContent} from '../../../../../layouts/right-drawer/right-drawer.component';
import {DateFilter} from 'src/app/shared/models/DateFilter';
import {MultiSelectFilter} from '../../../../../shared/components/multi-select-component';
import {FilterBarComponent} from '../../../../../shared/components/filter-bar/filter-bar.component';
import {FilterButtonComponent} from '../../../../../shared/components/buttons/filter-button/filter-button.component';
import {CardMenuItemComponent} from '../../../../../shared/components/card-menu-item/card-menu-item.component';
import {MainRoute, RootRoute} from '../../../../../shared/models/angular-routing';
import {InsightsRoute} from '../../../../insights/insights.routes';
import {LOCAL_STORAGE_ROUTE_ASSIGNMENT_VEHICLE_ID} from '../route-assignment/route-assignment.component';

const EVENT_TYPE_TO_LABEL = {
  [EventType.SHIFT_AUTO_END_TASK]: 'Shift Automatically Ended',
  [EventType.ROUTES_IMPORT_TASK]: 'Routes Imported',
  [EventType.STATIONARY_ALERT]: 'Stationary Alert',
  [EventType.ASSIGNMENT_REQUESTED]: 'Assignment Requested',
  [EventType.ASSIGNMENT_DECLINED]: 'Assignment Declined',
  [EventType.SHIFT_PAUSED]: 'Shift Paused',
  [EventType.SHIFT_RESUMED]: 'Shift Resumed',
  [EventType.NO_LONGER_STATIONARY]: 'No Longer Stationary',
  [EventType.EMAIL_EVENT]: 'Email Event',
};

@Component({
  selector: 'app-alerts',
  templateUrl: './alerts.component.html',
  styleUrl: './alerts.component.scss',
  imports: [
    FilterButtonComponent,
    FilterBarComponent,
    CardMenuItemComponent,
  ],
  providers: [
    ShortDateOrTimePipe,
  ]
})
export class AlertsComponent extends RightPanel implements OnInit, OnDestroy {

  eventRecords: EventRecord[];
  items: ActionMenuItem[];
  initialAction = false;

  dateFilter: DateFilter;
  driverFilter: MultiSelectFilter<number>;
  vehicleFilter: MultiSelectFilter<number>;

  @ViewChild(FilterBarComponent) filterBar: FilterBarComponent;

  constructor(
    protected router: Router,
    protected activatedRoute: ActivatedRoute,
    protected configurationService: ConfigurationService,
    protected assetsManager: AssetsManagerService,
    private readonly eventManager: EventManagerService,
    private readonly shortDateOrTimePipe: ShortDateOrTimePipe,
  ) {
    super(
      DrawerContent.ALERTS,
      router,
      activatedRoute,
      configurationService,
      assetsManager,
    );
  }

  ngOnInit(): void {
    this.initialize();
    this.loadConfiguration();

    const eventSubscription = this.eventManager.events$.subscribe(events => {
      this.eventRecords = events;
      if (this.initialAction) {
        this.onAssetAction();
        this.initialAction = false;
      } else {
        this.eventsToItems();
      }
    });
    this.openSubscriptions.push(eventSubscription);

    // all events read up to now
    this.eventManager.setAllEventsReadUpToNow();
  }

  ngOnDestroy(): void {
    this.unsubscribe();
    this.eventRecords = undefined;
    this.items = undefined;
  }

  onAssetsUpdate() {
    // do nothing
  }

  onAssetChange() {
    // do nothing
  }

  onAssetAction() {
    if (this.eventRecords) {
      if (this.asset) {
        const vehicle = this.assetsManager.getVehicles().find(v => v.id === this.asset.id);
        if (this.filterBar) {
          this.filterBar.overrideVehicleFilter([vehicle]);
        } else {
          setTimeout(
            () => this.filterBar?.overrideVehicleFilter([vehicle]),
            1000,
          );
        }
      }
    } else {
      this.initialAction = true;
    }
  }

  onRouteAction() {
    // do nothing
  }

  onConfigurationLoad() {
    // do nothing
  }

  onDateFilterChanged(dateFilter: DateFilter) {
    this.dateFilter = dateFilter;
    this.eventsToItems();
  }

  onDriverFilterChanged(driverFilter: MultiSelectFilter<number>) {
    this.driverFilter = driverFilter;
    this.eventsToItems();
  }

  onVehicleFilterChanged(vehicleFilter: MultiSelectFilter<number>) {
    this.vehicleFilter = vehicleFilter;
    this.eventsToItems();
  }

  private eventsToItems() {
    if (!!this.eventRecords && this.driverFilter && this.vehicleFilter && this.dateFilter) {
      const filteredEvents = this.eventRecords.filter(event =>
        (!this.driverFilter.elements || this.driverFilter.elements.includes(event.driverId)) &&
        (!this.vehicleFilter.elements || this.vehicleFilter.elements.includes(event.vehicleId)) &&
        (!this.dateFilter.from || this.dateFilter.from.isBefore(event.created)) &&
        (!this.dateFilter.to || this.dateFilter.to.isAfter(event.created))
      );

      this.items = filteredEvents.map(event => this.eventRecordToItem(event));
    }
  }

  private eventRecordToItem(event: EventRecord): ActionMenuItem {
    const {id, name, message, created, shiftId, vehicleId} = event;
    const subMenus = [];

    if (event.name === EventType.ASSIGNMENT_REQUESTED || event.name === EventType.ASSIGNMENT_DECLINED) {
      if (vehicleId) {
        subMenus.push(new ActionMenuItemSubMenu(
            'route',
            'Dismiss And Open Route Assignment',
            () => {
              this.openAssignment(vehicleId).then(() => {
                this.dismissEvent(id);
              });
            }
          )
        );
      }
    } else if (shiftId) {
      const shouldDismiss = event.name === EventType.SHIFT_AUTO_END_TASK;
      subMenus.push(
        new ActionMenuItemSubMenu(
          'open_in_new',
          shouldDismiss ? 'Dismiss And Open Shift Detail' : 'Open Shift Detail',
          () => {
            this.openShiftDetail(shiftId).then(() => {
              if (shouldDismiss) {
                this.dismissEvent(id);
              }
            });
          }
        )
      );
    }

    return new ActionMenuItem(
      id,
      'notifications',
      EVENT_TYPE_TO_LABEL[name] ?? name,
      message,
      this.shortDateOrTimePipe.transform(new Date(created)),
      null,
      null,
      () => false,
      null,
      null,
      () => false,
      null,
      null,
      subMenus,
    );
  }

  private async openShiftDetail(shiftId: number) {
    return this.router.navigate(
      ['/', RootRoute.MAIN, MainRoute.INSIGHTS, InsightsRoute.SHIFT_REPORT, InsightsRoute.SHIFT_DETAIL, shiftId],
      {queryParams: {drawer: 'alerts'}},
    );
  }

  private dismissEvent(eventId: number) {
    this.eventManager.dismissEvent(eventId);
  }

  private openAssignment(vehicleId: number) {
    localStorage.setItem(LOCAL_STORAGE_ROUTE_ASSIGNMENT_VEHICLE_ID, String(vehicleId));
    return this.router.navigate([], {queryParams: {drawer: 'route'}});
  }
}
